import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useBoolean,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { EPollType } from '@/client/legacy-shared-modules/poll/schema';
import { PostingModalFooter } from '@/components/PostingExperience/components/PostingModal/components/PostingModalFooter';
import { PollForm } from '@/components/post-form/PollForm';
import { SchedulePostMenu } from '@/components/post-form/SchedulePostMenu';
import { usePermissions } from '@/hooks/usePermissions';
import { content_edited } from '@/analytics';
import { minutesSince } from '@/analytics/contentAnalysis';

import { EditPollModal_PollFragment } from './graphql-types/EditPollModal_PollFragment';
import {
  EditPollModal_updateDraftPoll,
  EditPollModal_updateDraftPollVariables,
} from './graphql-types/EditPollModal_updateDraftPoll';

const FRAGMENT = gql`
  fragment EditPollModal_PollFragment on PollV2 {
    _id
    datePublished
    dateCreated
    scheduledPublishDate
    isAnnouncementScheduled

    title
    pollBody: description
    options {
      description
    }

    group {
      _id
    }
    tags {
      _id
      name
    }
    sponsorData {
      isSponsoredPost
    }
    organization {
      _id
    }
  }
`;

const UPDATE_POLL = gql`
  mutation EditPollModal_updateDraftPoll($input: UpdateDraftPollInput_V2!) {
    updateDraftPollV2(input: $input) {
      data {
        _id
        ...EditPollModal_PollFragment
      }
    }
  }
  ${FRAGMENT}
`;

export type PollFormValues = {
  title: string;
  group: string;
  options: { label: string }[];
  body: EditorState;
  isSponsoredPost: boolean;
  isAnnouncement: boolean;
  tags?: { _id: string; name: string }[];
  scheduledPublishDate: Date | null;
};

export const EditPollModal: React.FC<{
  post: EditPollModal_PollFragment;
  isOpen: boolean;
  onClose: () => void;
}> & { pollFragment: DocumentNode } = (props) => {
  const toast = useToast();
  const [loading, setLoading] = useBoolean();
  const isPublished = !!props.post.datePublished;
  const permissions = usePermissions();
  const canSchedulePosts = permissions.canDoAdminStuffAndAuthenticated;

  const methods = useForm<PollFormValues>({
    defaultValues: {
      title: props.post.title,
      body: props.post.pollBody
        ? EditorState.createWithContent(convertFromRaw(props.post.pollBody))
        : EditorState.createEmpty(),
      group: props.post.group?._id,
      options: props.post.options?.length
        ? props.post.options.map((option) => ({ label: option.description }))
        : [{ label: '' }, { label: '' }],
      isSponsoredPost: !!props.post.sponsorData?.isSponsoredPost,
      tags: props.post.tags ?? [],
      scheduledPublishDate: props.post.scheduledPublishDate
        ? new Date(props.post.scheduledPublishDate)
        : null,
      isAnnouncement: props.post.isAnnouncementScheduled,
    },
  });
  const { handleSubmit, formState } = methods;

  const [editPoll] = useMutation<
    EditPollModal_updateDraftPoll,
    EditPollModal_updateDraftPollVariables
  >(UPDATE_POLL, {
    onCompleted: (data) => {
      const updatedPoll = data.updateDraftPollV2.data;

      toast({
        description: !!updatedPoll?.scheduledPublishDate
          ? `Poll scheduled for ${dayjs(
              updatedPoll.scheduledPublishDate
            ).format('MMM D, h:mm A')}`
          : 'Poll updated',
        status: 'success',
      });

      setLoading.off();
      props.onClose();
    },
    onError: () => {
      toast({
        status: 'error',
        description: 'Failed to update poll',
      });
      setLoading.off();
    },
  });

  const onSubmit = async (params: PollFormValues) => {
    setLoading.on();

    await editPoll({
      variables: {
        input: {
          pollPostId: props.post._id,
          data: {
            title: params.title,
            body: convertToRaw(params.body.getCurrentContent()),
            type: EPollType.MULTIPLE_CHOICE,
            options: params.options
              .filter((option) => !!option.label)
              .map((option, i) => {
                return {
                  description: option.label,
                  responses: [],
                  order: i + 1,
                };
              }),
            tagIds: params.tags?.map((tag) => tag._id),
            scheduledPublishDate:
              params.scheduledPublishDate?.toISOString() ?? null,
            groupId: params.group,
          },
          isAnnouncement: params.isAnnouncement,
        },
      },
    });

    // Track content edit
    content_edited({
      entity: {
        id: props.post._id,
        type: 'post',
      },
      content: {
        contentType: 'post',
      },
      timing: {
        timeSinceCreation: (props.post as any).dateCreated ? minutesSince((props.post as any).dateCreated) : 0,
      },
    });
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={() => {
        methods.reset();
        props.onClose();
      }}
      size="xl"
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Draft Poll</ModalHeader>
        <ModalCloseButton />
        <ModalBody px="6" py="4">
          <FormProvider {...methods}>
            <PollForm
              organizationId={props.post.organization?._id}
              initialGroupId={props.post.group?._id}
              isEditing
            />
          </FormProvider>
        </ModalBody>

        <PostingModalFooter>
          <HStack>
            {!isPublished && canSchedulePosts && (
              <Controller
                name="scheduledPublishDate"
                control={methods.control}
                render={({ field: { onBlur, onChange, ref, value, name } }) => (
                  <SchedulePostMenu
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                    }}
                    onBlur={onBlur}
                    ref={ref}
                    isDisabled={
                      (formState.isSubmitted && !formState.isValid) || loading
                    }
                    validate={() => {
                      if (!value) {
                        methods.setError(name, {
                          message: 'A date and time must be specified',
                        });
                        return false;
                      } else {
                        methods.clearErrors(name);
                        return true;
                      }
                    }}
                    validationMessage={
                      formState.errors.scheduledPublishDate?.message
                    }
                    shouldRestrictUnsettingValue
                  />
                )}
              />
            )}
            <Button
              isLoading={loading}
              isDisabled={formState.isSubmitted && !formState.isValid}
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </HStack>
        </PostingModalFooter>
      </ModalContent>
    </Modal>
  );
};

EditPollModal.pollFragment = FRAGMENT;
